import { Injectable } from '@angular/core';
import { Device, Discovery, Lifecycle, Metrics, Parameters } from '@firebolt-js/sdk';

declare const window: Window &
  typeof globalThis & {
    appBuildVersion: string;
  };

@Injectable({
  providedIn: 'root',
})
export class FireboltService {
  /**
   * Age policy signalled by the platform via the launch / deeplink NavigationIntent
   * (intent.context.agePolicy). Forwarded opaquely on taggable Metrics calls.
   * Stays undefined when the platform sends no signal.
   */
  private sessionAgePolicy?: Parameters.AgePolicy;

  /** Resolves once init() has processed the launch intent (agePolicy capture included). */
  private initPromise?: Promise<void>;

  constructor() {}

  /**
   * Platform-provided age policy for the current session; single source of truth
   * for "reason to believe the viewer is a child". Undefined when no signal was sent.
   */
  get agePolicy(): Parameters.AgePolicy | undefined {
    return this.sessionAgePolicy;
  }

  /**
   * Call the initialization function
   * TODO: start to listen the background-foreground changes
   */
  init(): Promise<void> {
    this.initPromise ??= this.doInit();
    return this.initPromise;
  }

  /**
   * Resolve the session agePolicy only after the launch intent has been processed,
   * so consumers (e.g. ad tagging) cannot race the capture of a deeplink-delivered signal.
   */
  async getAgePolicy(): Promise<Parameters.AgePolicy | undefined> {
    await this.initPromise?.catch(() => undefined);
    return this.sessionAgePolicy;
  }

  /**
   * Marks the app ready to show to the user
   */
  async ready(): Promise<void> {
    await Lifecycle.ready().catch(() => {
      console.log('Error on Lifecycle Ready');
    });
    await Lifecycle.listen('unloading', () => {
      Lifecycle.clear();
    }).catch(() => {
      console.log('Error on Clear Listener');
    });
    await Metrics.appInfo(window.appBuildVersion);
  }

  /**
   * Close the app by application normal ending
   */
  async close(): Promise<void> {
    await Lifecycle.close(Lifecycle.CloseReason.DONE);
  }

  /**
   * Close the app by user action
   */
  async exit(): Promise<void> {
    await Lifecycle.close(Lifecycle.CloseReason.USER_EXIT);
  }

  /**
   * Close the app by remote exit button
   */
  async closeByButton(): Promise<void> {
    await Lifecycle.close(Lifecycle.CloseReason.REMOTE_BUTTON);
  }

  /**
   * Close the app by error
   */
  async closeWithError(): Promise<void> {
    await Lifecycle.close(Lifecycle.CloseReason.ERROR);
  }

  async getDeviceName(): Promise<string> {
    return await Device.model();
  }

  async getDeviceManufacturer(): Promise<string> {
    return await Device.make();
  }

  async getDeviceDistributor(): Promise<string> {
    return await Device.distributor();
  }

  async getDevicePlatform(): Promise<string> {
    return await Device.platform();
  }

  async getDeviceVersion(): Promise<string> {
    const deviceVersion = await Device.version(); // Device.version() returns a Promise<DeviceVersion>

    // Returns the SDK Version
    return deviceVersion?.sdk?.readable;
  }

  async getDeviceOS(): Promise<{ name: string; version: string }> {
    const versionObject = await Device.version();
    if (!versionObject?.os) return { name: 'Firebolt OS', version: '0.1.0' }; // Default value
    const osRead = versionObject.os.readable;
    const hasVersionDetails = versionObject.os.major && versionObject.os.minor && versionObject.os.patch;
    return {
      name: osRead.includes('v') ? osRead.split('v')[0].trimEnd() : 'Firebolt OS',
      version: hasVersionDetails ? `${versionObject.os.major}.${versionObject.os.minor}.${versionObject.os.patch}` : '0.1.0',
    };
  }

  /**
   *
   * @param errorType
   * @param errorCode
   * @param errorMessage
   * @param isVisible
   */
  async reportError(errorType: string, errorCode: string, errorMessage: string, isVisible: boolean = true): Promise<void> {
    await Metrics.error(
      (errorType as Metrics.ErrorType) ?? Metrics.ErrorType.OTHER,
      errorCode,
      errorMessage,
      isVisible,
      undefined,
      this.sessionAgePolicy
    );
  }

  /**
   * Report an error
   */
  async reportPlayerError(errorCode: string | number, errorMessage, isVisible = true): Promise<void> {
    await Metrics.error(Metrics.ErrorType.MEDIA, errorCode?.toString(), errorMessage, isVisible, undefined, this.sessionAgePolicy);
  }

  /**
   * Report player start playing content
   */
  async startContent(): Promise<void> {
    await Metrics.startContent(undefined, this.sessionAgePolicy);
  }

  /**
   * Report player stop playing content
   */
  async stopContent(): Promise<void> {
    await Metrics.stopContent(undefined, this.sessionAgePolicy);
  }

  async listenToInactive(callbackWhenInactive: () => void): Promise<number> {
    return await Lifecycle.listen('inactive', callbackWhenInactive);
  }

  /**
   * Capture the platform-provided agePolicy from a NavigationIntent's context, if present.
   * Keeps the previously captured value when the new intent carries no agePolicy.
   *
   * @param intent launch or deeplink NavigationIntent
   */
  private async doInit(): Promise<void> {
    // response contains lmt and privacy policy info, plus the launch NavigationIntent
    const initialization = await Parameters.initialization();
    this.updateAgePolicyFromIntent(initialization?.discovery?.navigateTo);

    // Respect agePolicy passed on warm/runtime deeplink intents as well.
    await Discovery.listen('navigateTo', (intent) => {
      this.updateAgePolicyFromIntent(intent);
    }).catch(() => {
      console.log('Error on navigateTo Listener');
    });
  }

  private updateAgePolicyFromIntent(intent?: { context?: { agePolicy?: Parameters.AgePolicy } }): void {
    const agePolicy = intent?.context?.agePolicy;
    if (agePolicy) {
      this.sessionAgePolicy = agePolicy;
    }
  }
}
